<template>
  <div>
    <h3>Configurações Gerais</h3>

    <div
      v-if="!userLoaded"
      class="text-center py-4"
    >
      <span
        class="spinner-border spinner-border-sm mr-2"
        role="status"
        aria-hidden="true"
      />
      <span class="text-muted">Carregando configurações...</span>
    </div>

    <div v-if="userLoaded && !isSuperAdmin">
      <config-fields
        v-model="userConfig"
        :show-photo-toggle="false"
        :show-admin-only-shift-rule="false"
      />
    </div>

    <div
      v-if="userLoaded && isSuperAdmin"
      class="row form-group"
    >
      <div class="col-md-12">
        <h5>Configurações por Empresa</h5>
        <p class="text-muted">
          Como super administrador, você pode configurar cada empresa individualmente
        </p>

        <div class="row mb-4">
          <div class="col-md-6">
            <label for="company_selector"><strong>Selecionar Empresa:</strong></label>
            <select
              id="company_selector"
              v-model="selectedCompanyId"
              class="form-control"
              @change="loadingCompanyConfiguration"
            >
              <option value="">
                Selecione uma empresa...
              </option>
              <option
                v-for="company in companiesList"
                :key="company.id"
                :value="company.id"
              >
                {{ company.company_name }}
              </option>
            </select>
          </div>
        </div>

        <div
          v-if="selectedCompanyId"
          class="card"
        >
          <div class="card-header">
            <h6 class="mb-0">
              Configurações para {{ selectedCompanyName }}
            </h6>
          </div>
          <div class="card-body">
            <config-fields
              v-model="selectedCompanyConfig"
              :show-admin-only-shift-rule="true"
            />
          </div>
        </div>

        <div
          v-else
          class="alert alert-info"
        >
          <i class="fa fa-info-circle" /> Selecione uma empresa acima para editar suas configurações.
        </div>
      </div>
    </div>

    <div
      v-if="userLoaded"
      class="row"
    >
      <div class="col-md-12">
        <button
          v-if="!isSuperAdmin || selectedCompanyId"
          class="btn btn-primary mt-2"
          :disabled="saveState === 'saving'"
          @click="saveConfiguration"
        >
          <span
            v-if="saveState === 'saving'"
            class="spinner-border spinner-border-sm mr-2"
            role="status"
            aria-hidden="true"
          />
          <span>{{ saveState === 'saving' ? 'Salvando...' : 'Salvar Configurações' }}</span>
        </button>
        <p
          v-else
          class="text-muted mt-2 mb-0"
        >
          Selecione uma empresa acima para editar e salvar suas configurações.
        </p>
        <div
          v-if="saveState === 'success'"
          class="alert alert-success mt-3"
        >
          <strong>Sucesso:</strong> {{ saveMessage || (isSuperAdmin ? `Configurações da empresa ${selectedCompanyName} salvas com sucesso` : 'Configurações salvas com sucesso') }}
        </div>
        <div
          v-if="saveState === 'error'"
          class="alert alert-danger mt-3"
        >
          <strong>Erro:</strong> {{ saveMessage || 'Erro ao salvar configurações' }}
        </div>
      </div>
    </div>
    <!-- Full page overlay shown while saving -->
    <div
      v-if="saveState === 'saving'"
      class="fullpage-overlay"
    >
      <div class="overlay-content">
        <div
          class="spinner-border text-light"
          role="status"
          aria-hidden="true"
        />
        <div class="mt-2 text-light">
          Salvando alterações...
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiErrorMessage } from '@/utils/apiError'

const ConfigFields = {
  name: 'ConfigFields',
  props: {
    value: { type: Object, required: true },
    showPhotoToggle: { type: Boolean, default: true },
    showAdminOnlyShiftRule: { type: Boolean, default: false },
  },
  data() {
    return {
      localDateStart: this.formatDateDisplay(this.value.date_start_period) || '',
      localDateEnd: this.formatDateDisplay(this.value.date_end_period) || '',
    }
  },
  watch: {
    'value.date_start_period': function (newVal) {
      this.localDateStart = this.formatDateDisplay(newVal) || ''
    },
    'value.date_end_period': function (newVal) {
      this.localDateEnd = this.formatDateDisplay(newVal) || ''
    },
  },
  computed: {
    photoSwitchId() {
      return `photo_verification_switch_${this._uid}`
    },
    periodDescription() {
      if (!this.value.date_start_period || !this.value.date_end_period) {
        return 'Período padrão: Dia 1 até o último dia do mês selecionado'
      }

      const start = this.formatDateDisplay(this.value.date_start_period)
      const end = this.formatDateDisplay(this.value.date_end_period)

      return `Período configurado: ${start} até ${end}`
    },
    periodExample() {
      if (!this.value.date_start_period || !this.value.date_end_period) {
        return 'Ex: Fevereiro = 01/02 até 28/02'
      }

      const extractDay = val => {
        if (val === null || val === undefined || val === '') return null
        const s = String(val)
        if (/^\d+$/.test(s)) {
          return parseInt(s, 10)
        }
        try {
          const d = new Date(s)
          if (!isNaN(d)) return d.getDate()
        } catch (e) {
          console.error('Erro ao extrair dia da data:', e)
        }
        return null
      }

      const startDay = extractDay(this.value.date_start_period)
      const endDay = extractDay(this.value.date_end_period)

      if (!startDay || startDay === 1) {
        return 'Ex: Fevereiro = 01/02 até 28/02'
      }

      return `Ex: Para Fevereiro, o período será de ${String(startDay).padStart(2, '0')}/02 até ${String(endDay || 28).padStart(2, '0')}/02`
    },
  },
  methods: {
    formatDateDisplay(dateStr) {
      if (!dateStr && dateStr !== 0) return ''

      if (/^\d+$/.test(String(dateStr))) {
        return String(dateStr)
      }

      if (/^\d{1,2}\/\d{1,2}(\/\d{2,4})?$/.test(String(dateStr))) {
        return String(dateStr)
      }

      const date = new Date(dateStr)
      if (isNaN(date)) {
        return String(dateStr)
      }

      return date.toLocaleDateString('pt-BR')
    },
    updateNumber(field, rawVal) {
      const val = rawVal === '' || rawVal === null || rawVal === undefined ? 0 : Number(rawVal)
      this.$emit('input', { ...this.value, [field]: isNaN(val) ? 0 : val })
    },
    updateDate(field, rawVal) {
      if (rawVal === '' || rawVal === null) {
        this.$emit('input', { ...this.value, [field]: null })
        return
      }

      if (/^\d+$/.test(String(rawVal))) {
        this.$emit('input', { ...this.value, [field]: parseInt(rawVal, 10) })
        return
      }

      this.$emit('input', { ...this.value, [field]: rawVal || null })
    },
    updateBoolean(field, event) {
      this.$emit('input', { ...this.value, [field]: event.target.checked })
    },
    updateString(field, rawVal) {
      this.$emit('input', { ...this.value, [field]: rawVal })
    },

    onDateInput(field, event) {
      const raw = event.target.value || ''
      const masked = this.applyDateMask(raw)
      if (field === 'date_start_period') {
        this.localDateStart = masked
      } else {
        this.localDateEnd = masked
      }
    },
    commitDate(field) {
      const val = field === 'date_start_period' ? (this.localDateStart || '').trim() : (this.localDateEnd || '').trim()
      if (!val) {
        this.updateDate(field, null)
        return
      }
      // If it's purely numeric (day) => pass number; else pass string like 'DD/MM' or 'DD/MM/YYYY'
      if (/^\d{1,4}$/.test(val)) {
        this.updateDate(field, parseInt(val, 10))
        return
      }
      // normalize slashes (remove extra spaces)
      const normalized = val.replace(/\s+/g, '')
      this.updateDate(field, normalized)
    },
    applyDateMask(input) {
      // keep only digits
      const digits = (input || '').replace(/[^0-9]/g, '')
      if (digits.length === 0) return ''
      if (digits.length <= 2) return digits
      if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
      // allow up to 8 digits for ddmmyyyy or ddmmyy
      return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`
    },
  },
  render(createElement) {
    const h = createElement

    // ---- Helpers de campo (layout consistente e espaçado) -------------------

    // Bloco de um campo: rótulo em negrito + controle + ajuda, sempre espaçado.
    const field = (label, control, help) => h('div', { class: 'form-group mb-4' }, [
      h('label', { class: 'font-weight-bold d-block mb-1' }, label),
      control,
      help ? h('small', { class: 'form-text text-muted' }, help) : null,
    ])

    const toggle = fieldName => {
      const id = `${fieldName}_switch_${this._uid}`
      const checked = !!this.value[fieldName]
      return h('div', { class: 'custom-control custom-switch' }, [
        h('input', {
          class: 'custom-control-input',
          attrs: { type: 'checkbox', id },
          domProps: { checked },
          on: { change: event => this.updateBoolean(fieldName, event) },
        }),
        h('label', { class: 'custom-control-label', attrs: { for: id } }, checked ? 'Ativado' : 'Desativado'),
      ])
    }

    const number = (fieldName, attrs = {}) => h('input', {
      class: 'form-control',
      attrs: { type: 'number', min: 0, ...attrs },
      domProps: { value: this.value[fieldName] },
      on: { input: event => this.updateNumber(fieldName, event.target.value) },
    })

    const textInput = (fieldName, localValue, placeholder) => h('input', {
      class: 'form-control',
      attrs: { type: 'text', placeholder, inputmode: 'numeric' },
      domProps: { value: localValue || '' },
      on: { input: event => this.onDateInput(fieldName, event), blur: () => this.commitDate(fieldName) },
    })

    const select = (fieldName, options, fallback) => h('select', {
      class: 'form-control',
      domProps: { value: this.value[fieldName] || fallback },
      on: { change: event => this.updateString(fieldName, event.target.value) },
    }, options.map(opt => h('option', { domProps: { value: opt.value } }, opt.label)))

    // Seção = card com ícone/título e um grid de itens (2 colunas). `extra` é
    // conteúdo full-width opcional abaixo do grid (ex.: alerta informativo).
    const section = (icon, title, items, extra, topExtra) => h('div', { class: 'card mb-4' }, [
      h('div', { class: 'card-header config-section-header' }, [
        h('h6', { class: 'mb-0' }, [h('i', { class: `fas ${icon} mr-2` }), title]),
      ]),
      h('div', { class: 'card-body' }, [
        topExtra || null,
        h('div', { class: 'row' }, items.filter(Boolean).map(item => h('div', { class: 'col-md-6' }, [item]))),
        extra || null,
      ]),
    ])

    const rows = []

    // 1) Período do Espelho de Ponto
    rows.push(section('fa-calendar-alt', 'Período do Espelho de Ponto', [
      field(
        'Data Inicial do Período',
        textInput('date_start_period', this.localDateStart, 'Ex: 25 ou 25/02 ou 25/02/2026'),
        'Informe um dia (ex: 25) ou data completa. Interpretado como início do período (mês atual).',
      ),
      field(
        'Data Final do Período',
        textInput('date_end_period', this.localDateEnd, 'Ex: 24 ou 24/02 ou 24/02/2026'),
        'Informe um dia (ex: 24) ou data completa. Interpretado como fim do período (mês atual).',
      ),
    ], h('div', { class: 'alert alert-info mb-0' }, [
      h('i', { class: 'fas fa-info-circle mr-2' }),
      h('strong', {}, this.periodDescription),
      h('br'),
      h('small', { class: 'text-muted' }, this.periodExample),
    ])))

    // 2) Tolerância de Ponto
    rows.push(section('fa-clock', 'Tolerância de Ponto', [
      field('Minutos de Tolerância Antes', number('tolerance_minutes_before'),
        'Quanto a batida pode adiantar do horário previsto sem gerar desvio.'),
      field('Minutos de Tolerância Depois', number('tolerance_minutes_after'),
        'Quanto a batida pode atrasar do horário previsto sem gerar desvio.'),
    ]))

    // 3) Jornada & Turnos
    rows.push(section('fa-business-time', 'Jornada & Turnos', [
      field(
        'Habilitar horas adicionais por turno',
        h('div', {}, [
          toggle('enable_max_hour_shift'),
          h('input', {
            class: 'form-control mt-2',
            attrs: {
              type: 'number', min: 1, max: 72, disabled: !this.value.enable_max_hour_shift, placeholder: 'Horas adicionais ao turno base',
            },
            domProps: { value: this.value.max_hour_shift },
            on: { input: event => this.updateNumber('max_hour_shift', event.target.value) },
          }),
        ]),
        'Só aplica quando habilitado. Ex: turno 24h + 1 = 25h; turno 8h + 1 = 9h.',
      ),
      this.showAdminOnlyShiftRule
        ? field('Separar turno 24h por intervalo de descanso (11h+)', toggle('respect_rest_interval_for_24h_shift'),
          'Ativado: pode quebrar turno 24h por descanso ≥ 11h. Desativado: mantém no mesmo turno dentro da janela máxima.')
        : null,
    ]))

    // 4) Batidas & Acesso
    const punchItems = []
    if (this.showPhotoToggle) {
      punchItems.push(field('Verificação de Foto', toggle('photo_verification'),
        'Exige reconhecimento facial na batida remota.'))
    }
    punchItems.push(field('Bloquear batida fora da escala/plantão', toggle('block_punch_outside_schedule'),
      'Ativado: quem não tem escala/plantão cobrindo o momento não bate ponto (presencial e remoto). Manual/admin não é afetado.'))
    punchItems.push(field('Minutos para deslogar usuário do aplicativo', number('minutes_for_logout_user'),
      'Tempo de inatividade até o app encerrar a sessão. 0 = desabilitado.'))
    rows.push(section('fa-fingerprint', 'Batidas & Acesso', punchItems))

    // 5) Aprovações & Hora Extra
    rows.push(section('fa-check-double', 'Aprovações & Hora Extra', [
      field('Exigir aprovação para troca de escala', toggle('enable_schedule_swap_approval'),
        'Ativado: a troca aguarda aprovação do superior. Desativado: troca direta.'),
      field('Permitir cancelamento de troca', toggle('enable_schedule_swap_cancellation'),
        'Ativado: usuários podem solicitar cancelamento de troca de escala já aprovada (fluxo com aceite do colega e aprovação da gestão).'),
      field('Exigir aprovação para troca de escala', toggle('enable_schedule_swap_approval'),
        'Ativado: a troca aguarda aprovação do superior. Desativado: troca direta.'),
      field('Enviar e-mails de escala', toggle('send_schedule_emails'),
        'Ativado: envia e-mails de escala (criação/exclusão, liberação, troca e alocação de plantão). Desativado: não envia nenhum e-mail de escala.'),
      field('Tratamento de hora extra',
        select('overtime_handling', [
          { value: 'payroll', label: 'Pagamento (paga as horas extras)' },
          { value: 'bank', label: 'Banco de horas (compensação)' },
        ], 'payroll'),
        'Destino da hora extra APROVADA. Em ambos os casos, a hora extra só é contabilizada após a aprovação do gestor.'),
    ]))

    // 6) Emails
    rows.push(section('fa-envelope', 'Emails', [
      field('Avisos de escala', toggle('send_schedule_emails'),
        'Ativado: envia e-mails de criação/remoção de escala, liberação e alocação de plantão.'),
      field('Avisos de troca de escala', toggle('send_swap_emails'),
        'Ativado: envia e-mails de aceite e de aprovação/reprovação de troca de escala.'),
    ]))

    // 7) Espelho de Ponto
    rows.push(section('fa-id-card', 'Espelho de Ponto', [
      field('Razão Social', toggle('show_company_name')),
      field('CNPJ', toggle('show_cnpj')),
      field('Nome', toggle('show_employee_name')),
      field('Data de Admissão', toggle('show_admission_date')),
      field('CTPS', toggle('show_ctps')),
      field('CPF', toggle('show_cpf')),
      field('PIS/PASEP', toggle('show_pis_pasep')),
      field('Função', toggle('show_role')),
      field('Departamento', toggle('show_department')),
      field('Período', toggle('show_period')),
      field('Feriados no Período', toggle('show_holidays')),
      field('Dias Úteis no Período', toggle('show_business_days')),
      field('Jornada de Trabalho', toggle('show_work_journey')),
      field('Total Trabalhado no Período', toggle('show_total_worked')),
      field('Total à Trabalhar no Período', toggle('show_total_to_work')),
      field('Banco de horas no período', toggle('show_hours_bank')),
      field('Total de intervalo no período', toggle('show_total_interval')),
      field('Hora extra não autorizada (N.A.)', toggle('show_unauthorized_overtime')),
    ], null, h('div', { class: 'col-md-12' }, [
      h('div', { class: 'form-group mb-2' }, [
        h('label', { class: 'font-weight-bold d-block mb-1' }, 'Aplicar esta personalização também no PDF do relatório'),
        toggle('mirror_pdf_follows_display_config'),
        h('small', { class: 'form-text text-muted' }, 'Ativado: o PDF gerado ao fechar o espelho respeita os mesmos campos ativados/desativados acima. Desativado: o PDF sempre mostra todos os campos, mesmo os ocultos na tela.'),
      ]),
      h('hr', { class: 'my-2' }),
    ])))

    return createElement('div', rows)
  },
}

export default {
  components: {
    ConfigFields,
  },
  data() {
    return {
      userConfig: {
        tolerance_minutes_before: 0,
        tolerance_minutes_after: 0,
        date_start_period: null,
        date_end_period: null,
        minutes_for_logout_user: 0,
        enable_schedule_swap_approval: false,
        enable_schedule_swap_cancellation: false,
        enable_max_hour_shift: false,
        max_hour_shift: null,
        respect_rest_interval_for_24h_shift: true,
        enable_schedule_swap_approval: false,
        block_punch_outside_schedule: false,
        overtime_handling: 'payroll',
        send_schedule_emails: true,
        send_swap_emails: true,
        photo_verification: false,
        show_company_name: true,
        show_cnpj: true,
        show_employee_name: true,
        show_admission_date: true,
        show_ctps: true,
        show_cpf: true,
        show_pis_pasep: true,
        show_role: true,
        show_department: true,
        show_period: true,
        show_holidays: true,
        show_business_days: true,
        show_work_journey: true,
        show_total_worked: true,
        show_total_to_work: true,
        show_hours_bank: true,
        show_total_interval: true,
        show_unauthorized_overtime: true,
        mirror_pdf_follows_display_config: true,
        id: 0,
      },
      pythonStatusId: null,
      companiesList: [],
      userLoaded: false,
      selectedCompanyId: '',
      selectedCompanyConfig: {
        tolerance_minutes_before: 0,
        tolerance_minutes_after: 0,
        date_start_period: null,
        date_end_period: null,
        minutes_for_logout_user: 0,
        enable_max_hour_shift: false,
        max_hour_shift: null,
        respect_rest_interval_for_24h_shift: true,
        enable_schedule_swap_approval: false,
        block_punch_outside_schedule: false,
        overtime_handling: 'payroll',
        send_schedule_emails: true,
        send_swap_emails: true,
        photo_verification: false,
        show_company_name: true,
        show_cnpj: true,
        show_employee_name: true,
        show_admission_date: true,
        show_ctps: true,
        show_cpf: true,
        show_pis_pasep: true,
        show_role: true,
        show_department: true,
        show_period: true,
        show_holidays: true,
        show_business_days: true,
        show_work_journey: true,
        show_total_worked: true,
        show_total_to_work: true,
        show_hours_bank: true,
        show_total_interval: true,
        show_unauthorized_overtime: true,
        mirror_pdf_follows_display_config: true,
        id: 0,
      },
      saveState: 'idle',
      saveMessage: '',
      saveTimeout: null,
    }
  },
  computed: {
    isSuperAdmin() {
      const user = this.$store.state.user || {}
      return Number(user.is_admin) === 1
    },
    selectedCompanyName() {
      const company = this.companiesList.find(company => company.id === this.selectedCompanyId)
      return company ? company.company_name : ''
    },
  },
  beforeDestroy() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
      this.saveTimeout = null
    }
  },
  mounted() {
    const user = this.$store.state.user || {}

    if (!user.id) {
      this.unwatchUser = this.$watch(
        () => (this.$store.state.user || {}).id,
        id => {
          if (!id) return
          if (this.unwatchUser) this.unwatchUser()
          this.iniciar()
        },
      )
      return
    }

    this.iniciar()
  },
  methods: {
    loadCompanies() {
      if (!this.isSuperAdmin) return
      const apiUrl = `${this.$store.getters.api}companies`
      axios.get(apiUrl)
        .then(response => {
          this.companiesList = response.data.data
        })
        .catch(error => {
          console.error('Erro ao carregar empresas:', error)
          this.showMessage('Erro', 'Erro ao carregar empresas', 'error')
        })
    },

    loadingCompanyConfiguration() {
      if (!this.selectedCompanyId) {
        this.clearCompanyConfiguration()
        return
      }
      const apiUrl = `${this.$store.getters.api}configurations/get-by-company`
      axios.get(apiUrl, { params: { company_id: this.selectedCompanyId } })
        .then(response => {
          const config = response.data.data?.[0]
          if (config) {
            this.selectedCompanyConfig = {
              id: config.id,
              tolerance_minutes_before: config.tolerance_minutes_before || 0,
              tolerance_minutes_after: config.tolerance_minutes_after || 0,
              date_start_period: config.date_start_period || null,
              date_end_period: config.date_end_period || null,
              photo_verification: config.photo_verification || false,
              minutes_for_logout_user: config.minutes_for_logout_user || 0,
              enable_schedule_swap_approval: !!config.enable_schedule_swap_approval,
              enable_schedule_swap_cancellation: !!config.enable_schedule_swap_cancellation,
              enable_max_hour_shift: !!config.enable_max_hour_shift,
              max_hour_shift: config.max_hour_shift || null,
              respect_rest_interval_for_24h_shift: typeof config.respect_rest_interval_for_24h_shift === 'boolean'
                ? config.respect_rest_interval_for_24h_shift
                : true,
              enable_schedule_swap_approval: !!config.enable_schedule_swap_approval,
              block_punch_outside_schedule: !!config.block_punch_outside_schedule,
              overtime_handling: config.overtime_handling || 'payroll',
              send_schedule_emails: config.send_schedule_emails !== false,
              send_swap_emails: config.send_swap_emails !== false,
              show_company_name: config.show_company_name !== false,
              show_cnpj: config.show_cnpj !== false,
              show_employee_name: config.show_employee_name !== false,
              show_admission_date: config.show_admission_date !== false,
              show_ctps: config.show_ctps !== false,
              show_cpf: config.show_cpf !== false,
              show_pis_pasep: config.show_pis_pasep !== false,
              show_role: config.show_role !== false,
              show_department: config.show_department !== false,
              show_period: config.show_period !== false,
              show_holidays: config.show_holidays !== false,
              show_business_days: config.show_business_days !== false,
              show_work_journey: config.show_work_journey !== false,
              show_total_worked: config.show_total_worked !== false,
              show_total_to_work: config.show_total_to_work !== false,
              show_hours_bank: config.show_hours_bank !== false,
              show_total_interval: config.show_total_interval !== false,
              show_unauthorized_overtime: config.show_unauthorized_overtime !== false,
              mirror_pdf_follows_display_config: config.mirror_pdf_follows_display_config !== false,
            }
          } else {
            this.clearCompanyConfiguration()
          }
        })
        .catch(error => {
          console.error('Erro ao carregar configuração da empresa:', error)
          this.clearCompanyConfiguration()
        })
    },

    clearCompanyConfiguration() {
      this.selectedCompanyConfig = {
        tolerance_minutes_before: 0,
        tolerance_minutes_after: 0,
        enable_schedule_swap_approval: false,
        enable_schedule_swap_cancellation: false,
        date_start_period: null,
        date_end_period: null,
        photo_verification: false,
        minutes_for_logout_user: 0,
        enable_max_hour_shift: false,
        max_hour_shift: null,
        respect_rest_interval_for_24h_shift: true,
        enable_schedule_swap_approval: false,
        block_punch_outside_schedule: false,
        overtime_handling: 'payroll',
        send_schedule_emails: true,
        send_swap_emails: true,
        show_company_name: true,
        show_cnpj: true,
        show_employee_name: true,
        show_admission_date: true,
        show_ctps: true,
        show_cpf: true,
        show_pis_pasep: true,
        show_role: true,
        show_department: true,
        show_period: true,
        show_holidays: true,
        show_business_days: true,
        show_work_journey: true,
        show_total_worked: true,
        show_total_to_work: true,
        show_hours_bank: true,
        show_total_interval: true,
        show_unauthorized_overtime: true,
        mirror_pdf_follows_display_config: true,
        id: 0,
      }
    },

    showMessage(titulo, texto, tipo) {
      this.$message(titulo, texto, tipo)
    },

    iniciar() {
      this.userLoaded = true

      if (this.isSuperAdmin) {
        this.loadCompanies()
        return
      }

      const apiUrl = `${this.$store.getters.api}configurations`

      axios.get(apiUrl)
        .then(response => {
          const { data } = response.data
          if (data.length > 0) {
            this.carregarUserConfig(data[0])
          }
        })
        .catch(error => {
          console.error('Erro ao carregar configurações:', error)
          this.showMessage('Erro', apiErrorMessage(error, 'Erro ao carregar configurações'), 'error')
        })
    },

    carregarUserConfig(data) {
      this.userConfig = {
        id: data.id,
        tolerance_minutes_before: data.tolerance_minutes_before,
        tolerance_minutes_after: data.tolerance_minutes_after,
        enable_schedule_swap_approval: !!data.enable_schedule_swap_approval,
        enable_schedule_swap_cancellation: !!data.enable_schedule_swap_cancellation,
        date_start_period: data.date_start_period || null,
        date_end_period: data.date_end_period || null,
        photo_verification: data.photo_verification || false,
        minutes_for_logout_user: data.minutes_for_logout_user || 0,
        enable_max_hour_shift: !!data.enable_max_hour_shift,
        max_hour_shift: data.max_hour_shift || null,
        respect_rest_interval_for_24h_shift: typeof data.respect_rest_interval_for_24h_shift === 'boolean'
          ? data.respect_rest_interval_for_24h_shift
          : true,
        enable_schedule_swap_approval: !!data.enable_schedule_swap_approval,
        block_punch_outside_schedule: !!data.block_punch_outside_schedule,
        overtime_handling: data.overtime_handling || 'payroll',
        send_schedule_emails: data.send_schedule_emails !== false,
        send_swap_emails: data.send_swap_emails !== false,
        show_company_name: data.show_company_name !== false,
        show_cnpj: data.show_cnpj !== false,
        show_employee_name: data.show_employee_name !== false,
        show_admission_date: data.show_admission_date !== false,
        show_ctps: data.show_ctps !== false,
        show_cpf: data.show_cpf !== false,
        show_pis_pasep: data.show_pis_pasep !== false,
        show_role: data.show_role !== false,
        show_department: data.show_department !== false,
        show_period: data.show_period !== false,
        show_holidays: data.show_holidays !== false,
        show_business_days: data.show_business_days !== false,
        show_work_journey: data.show_work_journey !== false,
        show_total_worked: data.show_total_worked !== false,
        show_total_to_work: data.show_total_to_work !== false,
        show_hours_bank: data.show_hours_bank !== false,
        show_total_interval: data.show_total_interval !== false,
        show_unauthorized_overtime: data.show_unauthorized_overtime !== false,
        mirror_pdf_follows_display_config: data.mirror_pdf_follows_display_config !== false,
      }
    },

    saveConfiguration() {
      if (this.isSuperAdmin && !this.selectedCompanyId) {
        this.showMessage('Atenção', 'Selecione uma empresa para salvar as configurações', 'warning')
        return
      }
      this.saveState = 'saving'
      this.saveMessage = ''

      if (this.isSuperAdmin) {
        const apiUrl = `${this.$store.getters.api}configurations/photo-verification-by-company`
        const configuracao = [{
          company_id: parseInt(this.selectedCompanyId, 10),
          tolerance_minutes_before: this.selectedCompanyConfig.tolerance_minutes_before,
          tolerance_minutes_after: this.selectedCompanyConfig.tolerance_minutes_after,
          date_start_period: this.selectedCompanyConfig.date_start_period,
          date_end_period: this.selectedCompanyConfig.date_end_period,
          photo_verification: this.selectedCompanyConfig.photo_verification,
          minutes_for_logout_user: this.selectedCompanyConfig.minutes_for_logout_user,
          enable_max_hour_shift: this.selectedCompanyConfig.enable_max_hour_shift,
          max_hour_shift: this.selectedCompanyConfig.max_hour_shift,
          respect_rest_interval_for_24h_shift: this.selectedCompanyConfig.respect_rest_interval_for_24h_shift,
          enable_schedule_swap_approval: this.selectedCompanyConfig.enable_schedule_swap_approval,
          enable_schedule_swap_cancellation: this.selectedCompanyConfig.enable_schedule_swap_cancellation,
          block_punch_outside_schedule: this.selectedCompanyConfig.block_punch_outside_schedule,
          overtime_handling: this.selectedCompanyConfig.overtime_handling,
          send_schedule_emails: this.selectedCompanyConfig.send_schedule_emails,
          send_swap_emails: this.selectedCompanyConfig.send_swap_emails,
          show_company_name: this.selectedCompanyConfig.show_company_name,
          show_cnpj: this.selectedCompanyConfig.show_cnpj,
          show_employee_name: this.selectedCompanyConfig.show_employee_name,
          show_admission_date: this.selectedCompanyConfig.show_admission_date,
          show_ctps: this.selectedCompanyConfig.show_ctps,
          show_cpf: this.selectedCompanyConfig.show_cpf,
          show_pis_pasep: this.selectedCompanyConfig.show_pis_pasep,
          show_role: this.selectedCompanyConfig.show_role,
          show_department: this.selectedCompanyConfig.show_department,
          show_period: this.selectedCompanyConfig.show_period,
          show_holidays: this.selectedCompanyConfig.show_holidays,
          show_business_days: this.selectedCompanyConfig.show_business_days,
          show_work_journey: this.selectedCompanyConfig.show_work_journey,
          show_total_worked: this.selectedCompanyConfig.show_total_worked,
          show_total_to_work: this.selectedCompanyConfig.show_total_to_work,
          show_hours_bank: this.selectedCompanyConfig.show_hours_bank,
          show_total_interval: this.selectedCompanyConfig.show_total_interval,
          show_unauthorized_overtime: this.selectedCompanyConfig.show_unauthorized_overtime,
          mirror_pdf_follows_display_config: this.selectedCompanyConfig.mirror_pdf_follows_display_config,
        }]
        axios.post(apiUrl, { configurations: configuracao })
          .then(() => {
            const msg = `Configurações da empresa ${this.selectedCompanyName} salvas com sucesso`
            this.saveState = 'success'
            this.saveMessage = msg
            this.showMessage('Sucesso', msg, 'success')
            this.loadingCompanyConfiguration()
            this.saveTimeout = setTimeout(() => { this.saveState = 'idle'; this.saveMessage = ''; this.saveTimeout = null }, 4000)
          })
          .catch(error => {
            console.error('Erro ao salvar configurações:', error)
            const msg = apiErrorMessage(error, 'Erro ao salvar configurações')
            this.saveState = 'error'
            this.saveMessage = msg
            this.showMessage('Erro', msg, 'error')
          })
        return
      }

      const apiUrl = `${this.$store.getters.api}configurations`
      const payload = {
        tolerance_minutes_before: this.userConfig.tolerance_minutes_before,
        tolerance_minutes_after: this.userConfig.tolerance_minutes_after,
        date_start_period: this.userConfig.date_start_period,
        date_end_period: this.userConfig.date_end_period,
        photo_verification: this.userConfig.photo_verification,
        minutes_for_logout_user: this.userConfig.minutes_for_logout_user,
        enable_max_hour_shift: this.userConfig.enable_max_hour_shift,
        enable_schedule_swap_approval: this.userConfig.enable_schedule_swap_approval,
        enable_schedule_swap_cancellation: this.userConfig.enable_schedule_swap_cancellation,
        max_hour_shift: this.userConfig.max_hour_shift,
        block_punch_outside_schedule: this.userConfig.block_punch_outside_schedule,
        overtime_handling: this.userConfig.overtime_handling,
        send_schedule_emails: this.userConfig.send_schedule_emails,
        send_swap_emails: this.userConfig.send_swap_emails,
        show_company_name: this.userConfig.show_company_name,
        show_cnpj: this.userConfig.show_cnpj,
        show_employee_name: this.userConfig.show_employee_name,
        show_admission_date: this.userConfig.show_admission_date,
        show_ctps: this.userConfig.show_ctps,
        show_cpf: this.userConfig.show_cpf,
        show_pis_pasep: this.userConfig.show_pis_pasep,
        show_role: this.userConfig.show_role,
        show_department: this.userConfig.show_department,
        show_period: this.userConfig.show_period,
        show_holidays: this.userConfig.show_holidays,
        show_business_days: this.userConfig.show_business_days,
        show_work_journey: this.userConfig.show_work_journey,
        show_total_worked: this.userConfig.show_total_worked,
        show_total_to_work: this.userConfig.show_total_to_work,
        show_hours_bank: this.userConfig.show_hours_bank,
        show_total_interval: this.userConfig.show_total_interval,
        show_unauthorized_overtime: this.userConfig.show_unauthorized_overtime,
        mirror_pdf_follows_display_config: this.userConfig.mirror_pdf_follows_display_config,
      }
      const request = this.userConfig.id === 0
        ? axios.post(apiUrl, payload)
        : axios.put(`${apiUrl}/${this.userConfig.id}`, payload)
      request
        .then(() => {
          const msg = 'Configurações salvas com sucesso'
          this.saveState = 'success'
          this.saveMessage = msg
          this.showMessage('Sucesso', msg, 'success')
          this.loadUserConfiguration()
          this.saveTimeout = setTimeout(() => { this.saveState = 'idle'; this.saveMessage = ''; this.saveTimeout = null }, 4000)
        })
        .catch(error => {
          console.error('Erro ao salvar configurações:', error)
          const msg = apiErrorMessage(error, 'Erro ao salvar configurações')
          this.saveState = 'error'
          this.saveMessage = msg
          this.showMessage('Erro', msg, 'error')
        })
    },
    loadUserConfiguration() {
      const apiUrl = `${this.$store.getters.api}configurations`
      axios.get(apiUrl)
        .then(response => {
          const { data } = response.data
          if (data && data.length > 0) {
            this.carregarUserConfig(data[0])
          }
        })
        .catch(errorResponse => {
          console.error('Erro ao recarregar configuração do usuário:', errorResponse)
        })
    },
  },
}
</script>

<style scoped>
.fullpage-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.overlay-content {
  text-align: center;
}
</style>

<style>

.config-section-header {
  background-color: #f8f9fa;
}
.dark-layout .config-section-header {
  background-color: #3b4253;
}
.dark-layout .config-section-header h6 {
  color: #d5dae0;
}
</style>
